import { Link, createFileRoute } from "@tanstack/react-router";
import {
  FileText,
  Layers,
  BookOpen,
  Building2,
  GraduationCap,
  Compass,
  Quote,
  Sparkles,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/admin/cih/")({
  staticData: { title: "Coaching Intelligence Hub" },
  component: CihDashboard,
});

/**
 * Grouped by the same three verticals the public site uses, so the admin
 * surface and the live information architecture stay in step.
 */
const verticals = [
  {
    title: "The Profession",
    description: "Who trains, certifies, and sets the standards for coaching",
    sections: [
      {
        to: "/admin/cih/ccos",
        label: "Organizations",
        description: "CCO content enrichment — accreditation bodies",
        icon: Building2,
      },
      {
        to: "/admin/cih/ctps",
        label: "Training Programs",
        description: "CTP content enrichment — program details",
        icon: GraduationCap,
      },
      {
        to: "/admin/cih/frameworks",
        label: "Frameworks",
        description: "V1 methodology and framework pages",
        icon: BookOpen,
      },
    ],
  },
  {
    title: "Guidance",
    description: "What clients are facing, and the proof coaching works",
    sections: [
      {
        to: "/admin/cih/challenges",
        label: "Challenges",
        description: "L1 pillar pages — coaching-for/[slug]",
        icon: Layers,
      },
      {
        to: "/admin/cih/articles",
        label: "Articles",
        description: "Coach article review queue",
        icon: FileText,
      },
      {
        to: "/admin/cih/stories",
        label: "Success Stories",
        description: "Client outcome review queue — public stories page",
        icon: Quote,
      },
    ],
  },
  {
    title: "Coach Business Resources",
    description: "How coaches build and grow their own practice",
    sections: [
      {
        to: "/admin/cih/guides",
        label: "Guides",
        description: "Coach Business Builder how-to content",
        icon: Compass,
      },
      {
        to: "/admin/cih/playbooks",
        label: "Playbooks",
        description: "Thought-leader-attributed business frameworks",
        icon: Sparkles,
      },
      {
        to: "/admin/cih/thought-leaders",
        label: "Thought Leaders",
        description: "Named authors behind playbooks",
        icon: Users,
      },
    ],
  },
] as const;

function CihDashboard() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-medium text-gray-900">
          Coaching Intelligence Hub
        </h1>
        <p className="mt-1 text-gray-500">
          Manage content across the profession, guidance, and coach business
          resources.
        </p>
      </div>
      {verticals.map((vertical) => (
        <section key={vertical.title} className="space-y-3">
          <div>
            <h2 className="text-lg font-medium text-gray-900">
              {vertical.title}
            </h2>
            <p className="text-sm text-gray-500">{vertical.description}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {vertical.sections.map((section) => (
              <Link
                key={section.to}
                to={section.to}
                className="group rounded-xl border border-gray-200 bg-white p-6 transition hover:border-purple-200 hover:shadow-sm"
              >
                <section.icon className="h-8 w-8 text-gray-400 transition-colors group-hover:text-purple-500" />
                <h3 className="mt-4 font-medium text-gray-900">
                  {section.label}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {section.description}
                </p>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
